import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import SyncStatusBanner from "../../components/client/SyncStatusBanner";
import { clientApi } from "../../api";
import { getToken } from "../../utils/authStorage";
import { formatDateAU } from "../../utils/formatDate";

const PRESETS = [7, 30, 60];

/**
 * Cards Expiring — surfaces the V_PUB_CARDS.CARDEXPIRYDATE from
 * S7000, which is what the gaming floor actually uses to enforce
 * card validity. This is distinct from the "Expiring Soon" report,
 * which tracks our platform's product-derived MEMBERSHIP expiry.
 */
export default function CardsExpiringReport() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [cache, setCache] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [venue, setVenue] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const d = await clientApi.getCardsExpiring(days);
      setRows(d.cards || []);
      setCache(d.cache || null);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { clientApi.getMe().then((me) => setVenue(me?.venue || null)).catch(() => {}); }, []);

  const providerLabel = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan", s7000: "S7000" }[venue?.provider_type] || "S7000";

  async function downloadCsv() {
    setError("");
    const token = getToken("client");
    const res = await fetch(clientApi.cardsExpiringCsvUrl(days), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return setError("CSV download failed. Please try again.");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cards-expiring-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Cards Expiring</h1>
          <p className="page-subtitle">Member cards whose {providerLabel} expiry falls in the next N days</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-outline" onClick={downloadCsv} disabled={!rows.length}>Download CSV</button>
        </div>
      </div>

      <SyncStatusBanner cache={cache} provider={venue?.provider_type} />

      <div style={{ display: "flex", gap: "0.5rem", margin: "1rem 0", alignItems: "center" }}>
        {PRESETS.map((d) => (
          <button key={d} className={`btn btn-sm ${days === d ? "btn-primary" : "btn-outline"}`} aria-pressed={days === d} onClick={() => setDays(d)}>Next {d} days</button>
        ))}
        <input
          type="number"
          aria-label="Days ahead"
          value={days}
          onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))}
          style={{ width: 90, padding: "0.4rem 0.6rem", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", borderRadius: "var(--radius-sm)" }}
        />
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? <div className="loading-state"><div className="spinner" /></div> : rows.length ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Card #</th>
                <th>Member #</th>
                <th>Name</th>
                <th>Card type</th>
                <th>Issued</th>
                <th>Expires</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.cardnumber}>
                  <td><code style={{ fontSize: "0.85rem" }}>{c.cardnumber}</code></td>
                  <td>
                    {c.membernumber
                      ? <Link to={`/client/members/${c.membernumber}`}><code>#{c.membernumber}</code></Link>
                      : "—"}
                  </td>
                  <td>{[c.firstname, c.surname].filter(Boolean).join(" ") || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{c.memcardtype || "—"}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{fmt(c.cardissuedate)}</td>
                  <td style={{ fontWeight: 600 }}>{fmt(c.cardexpirydate)}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{c.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No cards expiring in the next {days} days</h3>
          <p className="muted">Try widening the range, or check the sync status above.</p>
        </div>
      )}
    </ClientLayout>
  );
}

function fmt(v) {
  return formatDateAU(v);
}
